interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  stroke?: string;
  className?: string;
}

/**
 * Hand-rolled SVG sparkline. NO chart library. Renders a polyline + an end-cap
 * dot. Aero treatment: hot-pink stroke, rounded caps, soft pink-glow filter
 * inherited via `className="pink-glow"` on the caller.
 */
export default function Sparkline({
  data,
  width = 60,
  height = 16,
  stroke = "#FF1FA5",
  className,
}: SparklineProps) {
  if (!data || data.length === 0) {
    return (
      <svg width={width} height={height} className={className} aria-hidden="true">
        <line
          x1={0}
          x2={width}
          y1={height - 1}
          y2={height - 1}
          stroke="rgba(255,31,165,.25)"
          strokeWidth={1.25}
          strokeLinecap="round"
        />
      </svg>
    );
  }
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = data.length > 1 ? width / (data.length - 1) : width;
  const points = data
    .map((v, i) => {
      const x = i * stepX;
      const y = height - 1.5 - ((v - min) / range) * (height - 3);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  const last = data[data.length - 1];
  const lastX = (data.length - 1) * stepX;
  const lastY = height - 1.5 - ((last - min) / range) * (height - 3);

  return (
    <svg width={width} height={height} className={className} aria-hidden="true">
      <polyline
        fill="none"
        stroke={stroke}
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
      <circle cx={lastX} cy={lastY} r={2} fill={stroke} />
    </svg>
  );
}

/**
 * Stable pseudo-random sparkline data per project. Deterministic so layout
 * doesn't shift between renders.
 */
export function fakeSparkline(seed: string, length = 7): number[] {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const out: number[] = [];
  for (let i = 0; i < length; i++) {
    h = Math.imul(h ^ (h >>> 13), 16777619);
    const v = ((h >>> 0) % 1000) / 1000;
    out.push(v);
  }
  return out;
}
